<template>
  <div class="container mx-auto px-4 py-2">
    <h1 class="text-xl font-semibold mb-3">Отчёты ПСИ и ВХК</h1>

    <!-- Табы -->
    <div class="mb-3 border-b border-gray-200">
      <nav class="flex gap-6">
        <button
          type="button"
          :class="['px-1 py-3 border-b-2 text-sm font-medium',
            activeTab === 'psi' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700']"
          @click="switchTab('psi')"
        >
          Отчёт ПСИ
        </button>
        <button
          type="button"
          :class="['px-1 py-3 border-b-2 text-sm font-medium',
            activeTab === 'vhk' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700']"
          @click="switchTab('vhk')"
        >
          Отчёт ВХК
        </button>
      </nav>
    </div>

    <!-- Фильтры + создать -->
    <div class="flex flex-nowrap gap-3 items-end mb-3">
      <div class="shrink-0">
        <label class="block text-xs text-gray-600 mb-1">Дата с</label>
        <input type="date" v-model="filters.date_from" class="h-[39px] w-[150px] px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
      </div>
      <div class="shrink-0">
        <label class="block text-xs text-gray-600 mb-1">Дата по</label>
        <input type="date" v-model="filters.date_to" class="h-[39px] w-[150px] px-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
      </div>
      <div v-if="activeTab === 'psi'" class="flex-[2_1_0%] min-w-0">
        <label class="block text-xs text-gray-600 mb-1">Наименование изделия</label>
        <div class="v-select-filter otk-filter-select" style="width: 100%;">
          <v-select
            v-model="filters.product"
            :options="productOptions"
            label="name"
            :filterable="false"
            :loading="productLoading"
            placeholder="Все"
            @search="onSearchProducts"
            @open="onOpenProducts"
          >
            <template #option="option">
              <span>{{ option.sku ? option.sku + ' — ' + option.name : option.name }}</span>
            </template>
            <template #no-options="{ search }">
              <span v-if="!search">Введите символ для поиска…</span>
              <span v-else-if="productLoading">Поиск…</span>
              <span v-else>Не найдено</span>
            </template>
            <template #list-footer>
              <li v-if="productHasMore" class="px-3 py-2 text-center border-t">
                <button
                  type="button"
                  :disabled="productLoading"
                  class="text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                  @click.prevent.stop="loadMoreProducts"
                >
                  {{ productLoading ? 'Загрузка…' : 'Загрузить ещё…' }}
                </button>
              </li>
            </template>
          </v-select>
        </div>
      </div>
      <div v-if="activeTab === 'vhk'" class="flex-[2_1_0%] min-w-0">
        <label class="block text-xs text-gray-600 mb-1">Поставщик</label>
        <div class="v-select-filter otk-filter-select" style="width: 100%;">
          <v-select
            v-model="filters.supplier"
            :options="supplierOptions"
            label="name"
            :filterable="false"
            :loading="supplierLoading"
            placeholder="Все"
            @search="onSearchSuppliers"
            @open="onOpenSuppliers"
          >
            <template #no-options="{ search }">
              <span v-if="!search">Введите символ для поиска…</span>
              <span v-else-if="supplierLoading">Поиск…</span>
              <span v-else>Не найдено</span>
            </template>
            <template #list-footer>
              <li v-if="supplierHasMore" class="px-3 py-2 text-center border-t">
                <button
                  type="button"
                  :disabled="supplierLoading"
                  class="text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                  @click.prevent.stop="loadMoreSuppliers"
                >
                  {{ supplierLoading ? 'Загрузка…' : 'Загрузить ещё…' }}
                </button>
              </li>
            </template>
          </v-select>
        </div>
      </div>
      <div class="flex-1 min-w-0">
        <label class="block text-xs text-gray-600 mb-1">Кто проверил</label>
        <div class="v-select-filter otk-filter-select" style="width: 100%;">
          <v-select
            v-model="filters.inspector_id"
            :options="options.inspectors"
            :reduce="o => o.id"
            label="name"
            placeholder="Все"
          />
        </div>
      </div>
      <div class="shrink-0 flex gap-2">
        <button
          @click="printSelected"
          :disabled="selectedIds.length === 0"
          class="px-4 py-2 bg-indigo-600 text-white rounded text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
        >
          Печать
        </button>
        <button v-can-write="'otk_reports'" @click="openCreate" class="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700">
          Создать отчёт
        </button>
      </div>
    </div>

    <!-- Реестр -->
    <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <table class="w-full text-sm">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-3 py-2 text-left border-b w-8">
              <input type="checkbox" :checked="allSelected" @change="toggleAll($event)" />
            </th>
            <th @click="sortList('number')" class="px-3 py-2 text-left border-b cursor-pointer select-none whitespace-nowrap">
              № отчёта <span v-if="listSortBy === 'number'" class="text-blue-600">{{ listSortDir === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th @click="sortList('created_at')" class="px-3 py-2 text-left border-b cursor-pointer select-none whitespace-nowrap">
              Дата <span v-if="listSortBy === 'created_at'" class="text-blue-600">{{ listSortDir === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th @click="sortList('source')" class="px-3 py-2 text-left border-b cursor-pointer select-none whitespace-nowrap">
              Источник <span v-if="listSortBy === 'source'" class="text-blue-600">{{ listSortDir === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th @click="sortList('creator')" class="px-3 py-2 text-left border-b cursor-pointer select-none whitespace-nowrap">
              Создатель <span v-if="listSortBy === 'creator'" class="text-blue-600">{{ listSortDir === 'asc' ? '▲' : '▼' }}</span>
            </th>
            <th class="px-3 py-2 text-right border-b w-40">Действия</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="6" class="px-3 py-4 text-center text-gray-500">Загрузка...</td>
          </tr>
          <tr v-else-if="rows.length === 0">
            <td colspan="6" class="px-3 py-4 text-center text-gray-500">Отчётов нет</td>
          </tr>
          <tr v-for="row in rows" :key="row.id" class="border-t border-gray-200">
            <td class="px-3 py-2">
              <input type="checkbox" :checked="selectedIds.includes(row.id)" @change="toggleRow(row.id, $event)" />
            </td>
            <td class="px-3 py-2">{{ row.number }}</td>
            <td class="px-3 py-2">{{ row.created_at }}</td>
            <td class="px-3 py-2">{{ row.source }}</td>
            <td class="px-3 py-2">{{ row.creator }}</td>
            <td class="px-3 py-2 text-right whitespace-nowrap">
              <button @click="openPreview(row)" class="px-2 py-1 text-blue-600 hover:underline">Подробнее</button>
              <button v-can-write="'otk_reports'" @click="deleteReport(row)" class="px-2 py-1 text-red-600 hover:underline">Удалить</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Пагинация -->
    <div class="mt-3 flex items-center justify-between">
      <div class="text-sm text-gray-600">
        Всего: {{ pagination.total }}
      </div>
      <nav class="inline-flex -space-x-px">
        <button
          @click="changePage(pagination.currentPage - 1)"
          :disabled="pagination.currentPage <= 1"
          class="px-3 py-1.5 border border-gray-300 bg-white text-sm rounded-l disabled:opacity-50"
        >Назад</button>
        <span class="px-3 py-1.5 border-t border-b border-gray-300 bg-white text-sm">
          {{ pagination.currentPage }} / {{ pagination.lastPage }}
        </span>
        <button
          @click="changePage(pagination.currentPage + 1)"
          :disabled="pagination.currentPage >= pagination.lastPage"
          class="px-3 py-1.5 border border-gray-300 bg-white text-sm rounded-r disabled:opacity-50"
        >Вперёд</button>
      </nav>
    </div>

    <!-- Модалка отчёта: таблица строк снапшота -->
    <div v-if="previewVisible" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div class="bg-white rounded-lg shadow-lg w-[98vw] h-[95vh] flex flex-col">
        <div class="flex items-center justify-between px-5 py-3 border-b">
          <h2 class="text-lg font-bold">
            {{ activeTab === 'psi' ? 'Отчёт ПСИ' : 'Отчёт ВХК' }}<span v-if="previewNumber"> № {{ previewNumber }}</span>
          </h2>
          <div class="flex items-center gap-2">
            <button
              @click="exportExcel"
              :disabled="previewLoading"
              class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
            >
              Excel
            </button>
            <button
              @click="printReport"
              :disabled="previewLoading"
              class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50"
            >
              Печать
            </button>
            <button @click="closePreview" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300">
              Закрыть
            </button>
          </div>
        </div>
        <div class="flex-1 overflow-auto p-4 bg-white">
          <div v-if="previewLoading" class="h-full flex items-center justify-center text-gray-500">
            Загрузка отчёта...
          </div>
          <table v-else class="w-full text-sm border-collapse">
            <thead class="bg-gray-50">
              <tr>
                <th
                  v-for="col in previewColumns"
                  :key="col.key"
                  @click="sortPreview(col.key)"
                  :style="col.key === 'act' ? 'min-width: 180px' : ''"
                  class="px-3 py-2 text-left border border-gray-200 cursor-pointer select-none whitespace-nowrap"
                >
                  {{ col.label }}
                  <span v-if="sortKey === col.key" class="text-blue-600">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in sortedPreviewRows" :key="i" class="border-t border-gray-200">
                <td v-for="col in previewColumns" :key="col.key" class="px-3 py-2 border border-gray-200 align-top">
                  <a
                    v-if="col.key === 'act' && row.act_url"
                    :href="row.act_url"
                    target="_blank"
                    class="text-blue-600 hover:underline"
                  >{{ row.act || 'Скачать' }}</a>
                  <template v-else>{{ row[col.key] }}</template>
                </td>
              </tr>
              <tr v-if="sortedPreviewRows.length === 0">
                <td :colspan="previewColumns.length" class="px-3 py-4 text-center text-gray-500">Нет данных</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Модалка создания отчёта -->
    <div v-if="createVisible" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-[92%] max-w-[50rem] p-6 flex flex-col max-h-[90vh]">
        <h2 class="text-lg font-bold mb-1">
          {{ activeTab === 'psi' ? 'Создать отчёт ПСИ' : 'Создать отчёт ВХК' }}
        </h2>
        <p class="text-xs text-gray-500 mb-4">
          {{ activeTab === 'psi' ? 'Производственные заказы' : 'Приходные накладные (контроль пройден)' }} — можно выбрать несколько, в отчёте будет по одной таблице на каждый источник.
        </p>

        <!-- Фильтры: поиск + период создания -->
        <div class="flex flex-wrap items-end gap-2 mb-3">
          <div class="flex-1 min-w-[180px]">
            <label class="block text-xs text-gray-500 mb-1">Поиск</label>
            <input
              v-model="sourceSearch"
              @input="onSourceFilterChange"
              type="text"
              :placeholder="activeTab === 'psi' ? 'Номер заказа, продукция...' : 'Номер накладной, поставщик...'"
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label class="block text-xs text-gray-500 mb-1">Создан с</label>
            <input v-model="sourceDateFrom" @change="onSourceFilterChange" type="date" class="border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
          <div>
            <label class="block text-xs text-gray-500 mb-1">по</label>
            <input v-model="sourceDateTo" @change="onSourceFilterChange" type="date" class="border border-gray-300 rounded px-3 py-2 text-sm" />
          </div>
        </div>

        <!-- Управление выбором -->
        <div class="flex flex-wrap items-center gap-3 mb-2 text-sm">
          <button type="button" @click="selectAllOnPage" class="text-blue-600 hover:underline">Выбрать все</button>
          <button type="button" @click="clearSelected" class="text-gray-600 hover:underline">Сбросить все</button>
          <label class="inline-flex items-center gap-1 ml-auto cursor-pointer">
            <input type="checkbox" v-model="showOnlySelected" />
            Показать выбранные ({{ selectedSources.length }})
          </label>
        </div>

        <!-- Список с чекбоксами -->
        <div class="flex-1 overflow-y-auto border border-gray-200 rounded divide-y min-h-[200px]">
          <div v-if="loadingSources" class="px-3 py-4 text-center text-gray-500 text-sm">Загрузка...</div>
          <template v-else>
            <label
              v-for="item in visibleSources"
              :key="item.id"
              class="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
            >
              <input type="checkbox" :checked="isSourceSelected(item.id)" @change="toggleSource(item)" />
              <span class="flex-1">{{ item.label }}</span>
              <span class="text-gray-500 whitespace-nowrap">{{ item.created_at || '—' }}</span>
            </label>
            <div v-if="visibleSources.length === 0" class="px-3 py-4 text-center text-gray-500 text-sm">
              {{ showOnlySelected ? 'Ничего не выбрано' : 'Ничего не найдено' }}
            </div>
          </template>
        </div>

        <!-- Пагинация (в обычном режиме) -->
        <div v-if="!showOnlySelected" class="flex items-center justify-between mt-2 text-sm text-gray-600">
          <span>Всего: {{ sourceMeta.total }}</span>
          <div class="flex items-center gap-2">
            <button type="button" @click="changeSourcePage(sourceMeta.current_page - 1)" :disabled="sourceMeta.current_page <= 1" class="px-2 py-1 border rounded disabled:opacity-40">‹</button>
            <span>{{ sourceMeta.current_page }} / {{ sourceMeta.last_page }}</span>
            <button type="button" @click="changeSourcePage(sourceMeta.current_page + 1)" :disabled="sourceMeta.current_page >= sourceMeta.last_page" class="px-2 py-1 border rounded disabled:opacity-40">›</button>
          </div>
        </div>

        <div class="flex justify-end gap-2 mt-4">
          <button @click="closeCreate" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300">
            Отмена
          </button>
          <button
            @click="submitCreate"
            :disabled="selectedSources.length === 0 || creating"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {{ creating ? 'Формирование...' : 'Сформировать (' + selectedSources.length + ')' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'OtkReportsIndex',
  data() {
    return {
      activeTab: 'psi',
      rows: [],
      loading: false,
      filters: { date_from: '', date_to: '', number: '', product: null, supplier: null, inspector_id: null },
      options: { inspectors: [] },
      productOptions: [],
      productLoading: false,
      productSearchTimer: null,
      productSearch: '',
      productPage: 1,
      productHasMore: false,
      supplierOptions: [],
      supplierLoading: false,
      supplierSearchTimer: null,
      supplierSearch: '',
      supplierPage: 1,
      supplierHasMore: false,
      page: 1,
      perPage: 20,
      listSortBy: '',
      listSortDir: 'desc',
      pagination: { currentPage: 1, lastPage: 1, total: 0, perPage: 20 },
      selectedIds: [],
      suppressAutoApply: false,
      filterTimer: null,

      createVisible: false,
      creating: false,
      selectedSources: [],        // выбранные источники (объекты {id, label, ...})
      sourceItems: [],            // текущая страница списка источников
      loadingSources: false,
      sourceSearch: '',
      sourceDateFrom: '',
      sourceDateTo: '',
      sourcePage: 1,
      sourceMeta: { current_page: 1, last_page: 1, total: 0, per_page: 25 },
      showOnlySelected: false,
      sourceSearchTimer: null,

      previewVisible: false,
      previewLoading: false,
      previewRows: [],
      previewNumber: '',
      previewId: null,
      sortKey: '',
      sortDir: 'asc',
    };
  },
  computed: {
    allSelected() {
      return this.rows.length > 0 && this.rows.every((r) => this.selectedIds.includes(r.id));
    },
    // Что показываем в списке источников: все выбранные или текущую страницу
    visibleSources() {
      return this.showOnlySelected ? this.selectedSources : this.sourceItems;
    },
    // Колонки таблицы отчёта зависят от типа (ПСИ / ВХК)
    previewColumns() {
      if (this.activeTab === 'psi') {
        return [
          { key: 'num', label: '№' },
          { key: 'check_date', label: 'Дата проверки' },
          { key: 'product_name', label: 'Наименование' },
          { key: 'serial_number', label: 'Серийный номер' },
          { key: 'order_number', label: '№ заказа' },
          { key: 'result', label: 'Результат' },
          { key: 'reason', label: 'Причина несоответствия' },
          { key: 'inspector', label: 'Кто проверял' },
          { key: 'act', label: 'Акт' },
        ];
      }
      return [
        { key: 'num', label: '№' },
        { key: 'invoice_number', label: 'Номер накладной' },
        { key: 'receipt_date', label: 'Дата поступления' },
        { key: 'check_date', label: 'Дата проверки' },
        { key: 'product_name', label: 'Наименование' },
        { key: 'sku', label: 'Артикул' },
        { key: 'supplier', label: 'От кого' },
        { key: 'invoice_count', label: 'Кол-во по накладной' },
        { key: 'compliant_count', label: 'Соответствует' },
        { key: 'non_compliant_count', label: 'Не соответствует' },
        { key: 'certificate', label: 'Сертификат' },
        { key: 'inspector', label: 'Кто проверял' },
      ];
    },
    sortedPreviewRows() {
      const rows = [...this.previewRows];
      if (!this.sortKey) return rows;
      const key = this.sortKey;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      return rows.sort((a, b) => {
        const av = a[key] ?? '';
        const bv = b[key] ?? '';
        const an = Number(av);
        const bn = Number(bv);
        if (av !== '' && bv !== '' && !isNaN(an) && !isNaN(bn)) {
          return (an - bn) * dir;
        }
        return String(av).localeCompare(String(bv), 'ru') * dir;
      });
    },
  },
  watch: {
    'filters.date_from'() { this.scheduleApply(); },
    'filters.date_to'() { this.scheduleApply(); },
    'filters.product'() { this.scheduleApply(); },
    'filters.supplier'() { this.scheduleApply(); },
    'filters.inspector_id'() { this.scheduleApply(); },
  },
  mounted() {
    this.loadOptions();
    this.loadList();
  },
  methods: {
    // Автоприменение фильтров при изменении (без кнопки)
    scheduleApply() {
      if (this.suppressAutoApply) return;
      clearTimeout(this.filterTimer);
      this.filterTimer = setTimeout(() => {
        this.page = 1;
        this.loadList();
      }, 200);
    },
    switchTab(tab) {
      if (this.activeTab === tab) return;
      this.suppressAutoApply = true;
      this.activeTab = tab;
      this.page = 1;
      this.filters.product = null;
      this.filters.supplier = null;
      this.filters.inspector_id = null;
      this.productOptions = [];
      this.productHasMore = false;
      this.productPage = 1;
      this.supplierOptions = [];
      this.supplierHasMore = false;
      this.supplierPage = 1;
      this.loadOptions();
      this.loadList();
      this.$nextTick(() => { this.suppressAutoApply = false; });
    },
    async loadOptions() {
      try {
        const { data } = await axios.get('/api/otk-reports/filter-options', {
          params: { type: this.activeTab },
        });
        if (data?.success) {
          this.options = { inspectors: data.data.inspectors || [] };
        }
      } catch (e) {
        this.options = { inspectors: [] };
        console.error(e);
      }
    },

    // --- Асинхронная подгрузка номенклатуры ---
    onOpenProducts() {
      if (this.productOptions.length === 0) {
        this.loadProducts('');
      }
    },
    onSearchProducts(search) {
      clearTimeout(this.productSearchTimer);
      this.productSearchTimer = setTimeout(() => this.loadProducts(search || ''), 400);
    },
    loadMoreProducts() {
      this.productPage += 1;
      this.loadProducts(this.productSearch, true);
    },
    async loadProducts(search, append = false) {
      if (!append) {
        this.productPage = 1;
        this.productSearch = search || '';
      }
      this.productLoading = true;
      try {
        const { data } = await axios.post('/api/product/getProductsForSelect', {
          search: this.productSearch || undefined,
          per_page: 50,
          page: this.productPage,
        });
        const items = data?.data || [];
        this.productOptions = append ? [...this.productOptions, ...items] : items;
        this.productHasMore = data?.meta?.has_more || false;
      } catch (e) {
        if (!append) this.productOptions = [];
        console.error(e);
      } finally {
        this.productLoading = false;
      }
    },

    // --- Асинхронная подгрузка поставщиков ---
    onOpenSuppliers() {
      if (this.supplierOptions.length === 0) {
        this.loadSuppliers('');
      }
    },
    onSearchSuppliers(search) {
      clearTimeout(this.supplierSearchTimer);
      this.supplierSearchTimer = setTimeout(() => this.loadSuppliers(search || ''), 400);
    },
    loadMoreSuppliers() {
      this.supplierPage += 1;
      this.loadSuppliers(this.supplierSearch, true);
    },
    async loadSuppliers(search, append = false) {
      if (!append) {
        this.supplierPage = 1;
        this.supplierSearch = search || '';
      }
      this.supplierLoading = true;
      try {
        const { data } = await axios.post('/api/company/getCompaniesForSelect', {
          search: this.supplierSearch || undefined,
          per_page: 50,
          page: this.supplierPage,
        });
        const items = data?.data || [];
        this.supplierOptions = append ? [...this.supplierOptions, ...items] : items;
        this.supplierHasMore = data?.meta?.has_more || false;
      } catch (e) {
        if (!append) this.supplierOptions = [];
        console.error(e);
      } finally {
        this.supplierLoading = false;
      }
    },
    changePage(page) {
      if (page < 1 || page > this.pagination.lastPage) return;
      this.page = page;
      this.loadList();
    },
    // Сортировка реестра отчётов по колонке (клик по заголовку)
    sortList(key) {
      if (this.listSortBy !== key) {
        // 1-й клик — по возрастанию
        this.listSortBy = key;
        this.listSortDir = 'asc';
      } else if (this.listSortDir === 'asc') {
        // 2-й клик — по убыванию
        this.listSortDir = 'desc';
      } else {
        // 3-й клик — сброс к сортировке по умолчанию (новые сверху)
        this.listSortBy = '';
        this.listSortDir = 'desc';
      }
      this.page = 1;
      this.loadList();
    },
    async loadList() {
      this.loading = true;
      this.selectedIds = [];
      try {
        const { data } = await axios.get('/api/otk-reports', {
          params: {
            type: this.activeTab,
            date_from: this.filters.date_from || undefined,
            date_to: this.filters.date_to || undefined,
            number: this.filters.number || undefined,
            product_ids: this.activeTab === 'psi' && this.filters.product ? [this.filters.product.id] : undefined,
            supplier_ids: this.activeTab === 'vhk' && this.filters.supplier ? [this.filters.supplier.id] : undefined,
            inspector_ids: this.filters.inspector_id ? [this.filters.inspector_id] : undefined,
            page: this.page,
            per_page: this.perPage,
            sort_by: this.listSortBy || undefined,
            sort_dir: this.listSortDir,
          },
        });
        if (data?.success) {
          const d = data.data || {};
          this.rows = d.data || [];
          this.pagination = {
            currentPage: d.current_page || 1,
            lastPage: d.last_page || 1,
            total: d.total || 0,
            perPage: d.per_page || this.perPage,
          };
        }
      } catch (e) {
        this.notifyError(e, 'Не удалось загрузить список отчётов');
      } finally {
        this.loading = false;
      }
    },

    openCreate() {
      this.selectedSources = [];
      this.sourceItems = [];
      this.sourceSearch = '';
      this.sourceDateFrom = '';
      this.sourceDateTo = '';
      this.sourcePage = 1;
      this.showOnlySelected = false;
      this.sourceMeta = { current_page: 1, last_page: 1, total: 0, per_page: 25 };
      this.createVisible = true;
      this.loadSources();
    },
    closeCreate() {
      this.createVisible = false;
    },
    // Дебаунс-перезагрузка списка при изменении поиска/дат — всегда с первой страницы
    onSourceFilterChange() {
      clearTimeout(this.sourceSearchTimer);
      this.sourceSearchTimer = setTimeout(() => {
        this.sourcePage = 1;
        this.loadSources();
      }, 350);
    },
    async loadSources() {
      this.loadingSources = true;
      const url = this.activeTab === 'psi' ? '/api/otk-reports/psi-sources' : '/api/otk-reports/vhk-sources';
      try {
        const { data } = await axios.get(url, {
          params: {
            search: this.sourceSearch || '',
            date_from: this.sourceDateFrom || undefined,
            date_to: this.sourceDateTo || undefined,
            page: this.sourcePage,
            per_page: 25,
          },
        });
        this.sourceItems = data?.data || [];
        if (data?.meta) this.sourceMeta = data.meta;
      } catch (e) {
        this.sourceItems = [];
        this.notifyError(e, 'Не удалось загрузить список источников');
      } finally {
        this.loadingSources = false;
      }
    },
    changeSourcePage(p) {
      if (p < 1 || p > this.sourceMeta.last_page || p === this.sourceMeta.current_page) return;
      this.sourcePage = p;
      this.loadSources();
    },
    isSourceSelected(id) {
      return this.selectedSources.some((s) => s.id === id);
    },
    toggleSource(item) {
      if (this.isSourceSelected(item.id)) {
        this.selectedSources = this.selectedSources.filter((s) => s.id !== item.id);
      } else {
        this.selectedSources.push(item);
      }
    },
    // «Выбрать все» — добавляет все источники текущей страницы к выбранным
    selectAllOnPage() {
      for (const item of this.sourceItems) {
        if (!this.isSourceSelected(item.id)) this.selectedSources.push(item);
      }
    },
    clearSelected() {
      this.selectedSources = [];
    },
    async submitCreate() {
      if (!this.selectedSources || this.selectedSources.length === 0) return;
      this.creating = true;
      try {
        const url = this.activeTab === 'psi' ? '/api/otk-reports/psi' : '/api/otk-reports/vhk';
        const ids = this.selectedSources.map((s) => s.id);
        const payload = this.activeTab === 'psi'
          ? { production_order_ids: ids }
          : { receipt_invoice_ids: ids };
        const { data } = await axios.post(url, payload);
        if (data?.success) {
          this.closeCreate();
          this.page = 1;
          await this.loadList();
          this.loadOptions();
        } else {
          this.notifyError(null, data?.message || 'Не удалось сформировать отчёт');
        }
      } catch (e) {
        this.notifyError(e, 'Не удалось сформировать отчёт');
      } finally {
        this.creating = false;
      }
    },

    toggleRow(id, event) {
      if (event.target.checked) {
        if (!this.selectedIds.includes(id)) this.selectedIds.push(id);
      } else {
        this.selectedIds = this.selectedIds.filter((x) => x !== id);
      }
    },
    toggleAll(event) {
      this.selectedIds = event.target.checked ? this.rows.map((r) => r.id) : [];
    },

    async printSelected() {
      if (this.selectedIds.length === 0) return;
      try {
        const response = await axios.get('/api/otk-reports/print', {
          params: { type: this.activeTab, ids: this.selectedIds },
        });
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          this.notifyError(null, 'Разрешите всплывающие окна для печати');
          return;
        }
        printWindow.document.write(response.data);
        printWindow.document.close();
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            printWindow.onafterprint = () => printWindow.close();
          }, 500);
        };
      } catch (e) {
        this.notifyError(e, 'Не удалось открыть документ для печати');
      }
    },

    // Открытие отчёта: одна плоская таблица, строки всех источников со сквозной нумерацией «№»
    async openPreview(row) {
      this.previewVisible = true;
      this.previewLoading = true;
      this.previewRows = [];
      this.previewNumber = row.number || '';
      this.previewId = row.id;
      this.sortKey = '';
      this.sortDir = 'asc';
      try {
        const { data } = await axios.get(`/api/otk-reports/${row.id}`);
        const snapshot = data?.data?.snapshot || {};
        this.previewRows = Array.isArray(snapshot.rows) ? snapshot.rows : [];
      } catch (e) {
        this.notifyError(e, 'Не удалось загрузить отчёт');
        this.closePreview();
      } finally {
        this.previewLoading = false;
      }
    },
    closePreview() {
      this.previewVisible = false;
      this.previewRows = [];
      this.previewNumber = '';
      this.previewId = null;
    },
    sortPreview(key) {
      if (this.sortKey !== key) {
        // 1-й клик — по возрастанию
        this.sortKey = key;
        this.sortDir = 'asc';
      } else if (this.sortDir === 'asc') {
        // 2-й клик — по убыванию
        this.sortDir = 'desc';
      } else {
        // 3-й клик — сброс к исходному порядку строк
        this.sortKey = '';
        this.sortDir = 'asc';
      }
    },
    exportExcel() {
      if (!this.previewId) return;
      window.open(`/api/otk-reports/${this.previewId}/export`, '_blank');
    },
    async printReport() {
      if (!this.previewId) return;
      try {
        const response = await axios.get(`/api/otk-reports/${this.previewId}/print`, {
          responseType: 'text',
        });
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          this.notifyError(null, 'Разрешите всплывающие окна для печати');
          return;
        }
        printWindow.document.write(response.data);
        printWindow.document.close();
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            printWindow.onafterprint = () => printWindow.close();
          }, 500);
        };
      } catch (e) {
        this.notifyError(e, 'Не удалось открыть документ для печати');
      }
    },

    async deleteReport(row) {
      if (!confirm(`Удалить отчёт № ${row.number}?`)) return;
      try {
        await axios.delete(`/api/otk-reports/${row.id}`);
        await this.loadList();
      } catch (e) {
        this.notifyError(e, 'Не удалось удалить отчёт');
      }
    },

    notifyError(error, fallback) {
      const message = error?.response?.data?.message || fallback || 'Произошла ошибка, попробуйте ещё раз';
      if (error) {
        console.error(error);
      }
      alert(message);
    },
  },
};
</script>

<style>
/* Селект занимает всю ширину флекс-обёртки фильтра */
.otk-filter-select,
.otk-filter-select .v-select {
  width: 100%;
}

/* Длинное значение в фильтре-селекте — одной строкой с многоточием, не вылезает за рамки */
.otk-filter-select .vs__selected-options {
  flex-wrap: nowrap !important;
  overflow: hidden !important;
}

.otk-filter-select .vs__selected {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  max-width: calc(100% - 44px) !important;
}
</style>
